/* Casucci 2026 — caroselli prodotto della PDP: consigliati (<cs-recs>) e visti di recente (<cs-recently-viewed>).
   Vanilla, defer, nessuna dipendenza. Frecce: delegate in cs-sections.js ([data-cs-prev]/[data-cs-next]).
   Le card (cs-product-card) arrivano già renderizzate dal server; il quick add è delegato in cs-card.js. */
(() => {
  if (window.__csRecs) return;
  window.__csRecs = true;

  const STORE = 'cs:recent';
  const MAX_STORED = 13; // 12 + il prodotto corrente

  // Carica quando la sezione si avvicina al viewport. L'host è [hidden] (display:none): si osserva il wrapper della sezione.
  const whenNear = (host, fn) => {
    if (!('IntersectionObserver' in window)) return fn();
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((en) => en.isIntersecting)) return;
      io.disconnect();
      fn();
    }, { rootMargin: '600px 0px' });
    io.observe(host.parentElement || host);
  };

  // Mostra la lista nel carosello; frecce solo se c'è davvero qualcosa da scorrere.
  const mount = (host, list) => {
    const target = host.querySelector('[data-cs-recs-target]');
    const nav = host.querySelector('[data-cs-recs-nav]');
    const title = host.querySelector('.cs-recs__title');
    if (title) {
      title.id = title.id || `cs-recs-title-${Math.random().toString(36).slice(2, 8)}`;
      list.setAttribute('aria-labelledby', title.id);
    }
    list.classList.add('cs-carousel__track', 'cs-recs__track');
    list.setAttribute('role', 'list');
    target.replaceChildren(list);
    target.removeAttribute('aria-busy');
    host.hidden = false;
    host.closest('.shopify-section')?.classList.remove('cs-recs-empty');
    if (!nav) return;
    const update = () => { nav.hidden = list.scrollWidth <= list.clientWidth + 2; };
    requestAnimationFrame(update);
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(list);
  };

  const empty = (host) => {
    host.hidden = true;
    host.closest('.shopify-section')?.classList.add('cs-recs-empty');
  };

  const getHtml = async (url) => {
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(String(res.status));
    return res.text();
  };

  class CsRecs extends HTMLElement {
    connectedCallback() {
      if (this.loaded) return;
      this.loaded = true;
      whenNear(this, () => this.load());
    }

    async load() {
      try {
        const doc = new DOMParser().parseFromString(await getHtml(this.dataset.url), 'text/html');
        const list = doc.querySelector('[data-cs-recs-items]');
        if (!list || !list.children.length) return empty(this);
        mount(this, document.importNode(list, true));
      } catch (e) {
        empty(this);
      }
    }
  }

  class CsRecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (this.loaded) return;
      this.loaded = true;
      const current = this.dataset.handle;
      let list = [];
      try { list = JSON.parse(localStorage.getItem(STORE)) || []; } catch (e) { list = []; }
      if (!Array.isArray(list)) list = [];
      this.handles = list.filter((h) => h && h !== current).slice(0, Number(this.dataset.limit) || 8);
      if (current) this.save([current, ...list.filter((h) => h !== current)].slice(0, MAX_STORED));
      if (!this.handles.length) return empty(this);
      whenNear(this, () => this.load());
    }

    save(list) {
      try { localStorage.setItem(STORE, JSON.stringify(list)); } catch (e) { /* storage pieno o disattivato */ }
    }

    async load() {
      const root = this.dataset.root || '';
      const results = await Promise.all(this.handles.map(async (h) => {
        try {
          const html = await getHtml(`${root}/products/${encodeURIComponent(h)}?section_id=cs-card-render`);
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const li = doc.querySelector('li.cs-recs__item');
          // né card né marcatore "esiste" = prodotto rimosso/non pubblicato
          return { h, li, ok: !!(li || doc.querySelector('[data-cs-exists]')) };
        } catch (e) {
          return { h, li: null, ok: false }; // 404: prodotto rimosso o non più pubblicato
        }
      }));
      // Pulizia: via dallo storico i prodotti che non esistono più.
      const gone = results.filter((r) => !r.ok).map((r) => r.h);
      if (gone.length) {
        try {
          const all = JSON.parse(localStorage.getItem(STORE)) || [];
          this.save(all.filter((h) => !gone.includes(h)));
        } catch (e) { /* ignora */ }
      }
      const items = results.filter((r) => r.li).map((r) => document.importNode(r.li, true));
      if (!items.length) return empty(this);
      const ul = document.createElement('ul');
      ul.append(...items);
      mount(this, ul);
    }
  }

  if (!customElements.get('cs-recs')) customElements.define('cs-recs', CsRecs);
  if (!customElements.get('cs-recently-viewed')) customElements.define('cs-recently-viewed', CsRecentlyViewed);
})();
